package com.merkost.handpass.admin.pages

import androidx.compose.runtime.*
import com.merkost.handpass.admin.components.*
import com.merkost.handpass.admin.container.StatsContainer
import com.merkost.handpass.admin.contract.*
import com.merkost.handpass.sharedweb.components.WebIcon
import com.merkost.handpass.sharedweb.theme.*
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import com.merkost.handpass.admin.di.koinContainer
import pro.respawn.flowmvi.compose.dsl.subscribe

@Page("/stats")
@Composable
fun StatsPage() {
    AdminPageScaffold(title = "Stats", currentPath = "/stats", showTitle = false) {
        val container = koinContainer<StatsContainer>()
        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is StatsAction.Navigate -> navigate(action.path)
                }
            }
            when (val s = state) {
                StatsState.Loading -> StatsSkeleton()
                is StatsState.Error -> AdminEmptyState(
                    title = "Failed to load",
                    description = s.message,
                    actionLabel = "Retry",
                    onAction = { intent(StatsIntent.Refresh) },
                )
                is StatsState.Content -> StatsContent(s, onIntent = ::intent)
            }
        }
    }
}

@Composable
private fun StatGrid(content: @Composable () -> Unit) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(min(200px, 100%), 1fr))")
            gap(HandpassSpacing.Default)
            property("margin-bottom", "${HandpassSpacing.XL}")
        }
    }) { content() }
}

@Composable
private fun ActionMixBar(label: String, count: Long, maxCount: Long) {
    val percentWidth = ((count.toDouble() / maxCount.coerceAtLeast(1L).toDouble()) * 100.0).coerceIn(0.0, 100.0)

    Div({ style { padding(HandpassSpacing.SM, 0.px) } }) {
        Div({
            style {
                display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
                property("margin-bottom", "${HandpassSpacing.XS}")
            }
        }) {
            Span({ style { fontSize(HandpassTypography.Body); color(OnSurface.value()) } }) {
                Text(label)
            }
            AdminBadge(label = "$count", variant = AdminBadgeVariant.Neutral)
        }
        Div({
            style {
                width(100.percent)
                height(8.px)
                borderRadius(HandpassRadius.SM)
                property("background-color", HandpassColors.Border)
                property("overflow", "hidden")
            }
        }) {
            Div({
                style {
                    width(percentWidth.percent)
                    height(100.percent)
                    borderRadius(HandpassRadius.SM)
                    property("background-color", HandpassColors.Primary)
                }
            })
        }
    }
}

@Composable
private fun StatsSkeleton() {
    AdminPageHeader(title = "Stats", subtitle = "Moderation health at a glance")
    StatGrid {
        repeat(7) { AdminSkeletonCard() }
    }
    AdminPanel(title = "Recent activity") { AdminSkeletonTable(rows = 6) }
}

@Composable
private fun StatsContent(state: StatsState.Content, onIntent: (StatsIntent) -> Unit) {
    AdminPageHeader(
        title = "Stats",
        subtitle = "Moderation health at a glance",
        actions = {
            AdminButton(label = "Refresh", onClick = { onIntent(StatsIntent.Refresh) }, variant = AdminButtonVariant.Primary, size = AdminButtonSize.SM)
        },
    )

    StatGrid {
        StatCard(label = "Total Users", value = "${state.stats.totalUsers}", icon = WebIcon.Users)
        StatCard(label = "Active Listings", value = "${state.stats.activeListings}", icon = WebIcon.Listings)
        StatCard(
            label = "Pending Reports",
            value = "${state.stats.totalQueueSize}",
            icon = WebIcon.Queue,
            highlight = state.stats.totalQueueSize > 0,
            onClick = { onIntent(StatsIntent.NavigateToQueue) },
        )
        StatCard(label = "Actions Today", value = "${state.stats.actionsToday}", icon = WebIcon.Check)
        StatCard(
            label = "Oldest Unactioned",
            value = formatDuration(state.stats.oldestPendingReportAgeSeconds),
            icon = WebIcon.Bell,
            highlight = (state.stats.oldestPendingReportAgeSeconds ?: 0) > 24 * 60 * 60,
            onClick = { onIntent(StatsIntent.NavigateToQueue) },
        )
        StatCard(
            label = "Breaching SLA (>24h)",
            value = "${state.stats.pendingOver24h}",
            icon = WebIcon.Bell,
            highlight = state.stats.pendingOver24h > 0,
            onClick = { onIntent(StatsIntent.NavigateToQueue) },
        )
        StatCard(label = "Avg Time to Action (7d)", value = formatDuration(state.stats.avgTimeToActionSeconds), icon = WebIcon.Stats)
    }

    if (state.stats.actionMix.isNotEmpty()) {
        Div({ style { property("margin-bottom", "${HandpassSpacing.XL}") } }) {
            AdminPanel(title = "Action mix · 7 days") {
                state.stats.actionMix.forEach { entry ->
                    Div({
                        style {
                            display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
                            padding(HandpassSpacing.SM, 0.px)
                        }
                    }) {
                        Span({ style { fontSize(HandpassTypography.Body); color(OnSurface.value()) } }) {
                            Text(entry.action.replace("_", " ").replaceFirstChar { it.uppercase() })
                        }
                        AdminBadge(label = "${entry.count}", variant = AdminBadgeVariant.Neutral)
                    }
                }
            }
        }
    }

    if (state.stats.listingsByState.isNotEmpty()) {
        Div({ style { property("margin-bottom", "${HandpassSpacing.XL}") } }) {
            AdminPanel(title = "Active listings by state") {
                val maxCount = state.stats.listingsByState.maxOfOrNull { it.count }?.coerceAtLeast(1L) ?: 1L
                state.stats.listingsByState.forEach { entry ->
                    ActionMixBar(
                        label = entry.state,
                        count = entry.count,
                        maxCount = maxCount,
                    )
                }
            }
        }
    }

    AdminPanel(title = "Recent activity") {
        if (state.stats.recentActions.isEmpty()) {
            AdminEmptyState(title = "No recent activity")
        } else {
            state.stats.recentActions.forEach { action ->
                Div({
                    style {
                        display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center)
                        padding(HandpassSpacing.MD, 0.px)
                        property("border-bottom", "1px solid ${Border.value()}")
                    }
                }) {
                    Div {
                        Span({ style { fontWeight(500); fontSize(HandpassTypography.Body); color(OnSurface.value()) } }) { Text(action.adminName ?: "Unknown") }
                        Span({ style { color(OnSurfaceMuted.value()); fontSize(HandpassTypography.Body); property("margin-left", "${HandpassSpacing.SM}") } }) { Text(action.action.replace("_", " ")) }
                        action.targetName?.let { name ->
                            Span({ style { fontWeight(500); fontSize(HandpassTypography.Body); property("margin-left", "${HandpassSpacing.SM}"); color(OnSurface.value()) } }) { Text(name) }
                        }
                    }
                    Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceFaint.value()) } }) { Text(action.createdAt.take(16).replace("T", " ")) }
                }
            }
        }
    }
}

private fun formatDuration(seconds: Long?): String {
    if (seconds == null) return "—"
    if (seconds <= 0L) return "<1m"
    val days = seconds / 86400
    val hours = (seconds % 86400) / 3600
    val minutes = (seconds % 3600) / 60
    return when {
        days > 0 -> "${days}d ${hours}h"
        hours > 0 -> "${hours}h ${minutes}m"
        minutes > 0 -> "${minutes}m"
        else -> "<1m"
    }
}
